// js/AdminBoard/CustomerSupportBoard/DeviceDetail/Tabs/DeviceSettingsTab.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import CheckIcon from "@mui/icons-material/CheckRounded";
import XIcon from "@mui/icons-material/CloseRounded";
import EditIcon from "@mui/icons-material/EditRounded";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Switch from "@mui/material/Switch";
import dayjs from "dayjs";
import deviceProfile from "images/device.png";

import {
    updateKonnectID,
    fetchDeviceSettings,
    updateDeviceSetting,
    fetchAddressBooksForDevice,
    fetchAddressBookEntries,
    fetchDeviceImage,
} from "js/Api/index";
import ContactsReorderModal from "js/Component/ContactsReorderModal";

const SettingRowComponent = ({
    setting,
    editingSettingId,
    editingValue,
    setEditingValue,
    updatingSettings,
    handleToggleSetting,
    handleEditSetting,
    handleCancelEdit,
    handleSaveTextSetting,
    handleSelectChange,
    copyToClipboard,
    parseBooleanValue,
    isBooleanSetting,
    setDndSetting,
    initializeDndFromSetting,
    setShowDndModal,
    setOrderSetting,
    initializeOrderFromSetting,
    setShowOrderModal,
    setOrderKind,
    openAddressBookReorder,
    loadingReorder,
    linkedDevicesMap,
    handlePhotoGalleryChange,
}) => {
    const isBoolean = isBooleanSetting(setting.value);
    const isEditing = editingSettingId === setting.id;
    const isUpdating = updatingSettings[setting.id];
    const hasValue =
        setting.value === 0 ||
        setting.value === false ||
        (setting.value !== null &&
            setting.value !== undefined &&
            String(setting.value).trim() !== "");

    const isCarerMode = setting.friendlyName === "settings.group_leader.carer.mode";
    const isMaxContacts = setting.friendlyName === "settings.max.contacts_per_page";
    const isDndSetting = setting.friendlyName === "settings.dnd";
    const isOrderSetting = setting.friendlyName === "settings.device_contact_order";
    const isAddressBookOrderSetting =
        setting.friendlyName === "settings.device_address_book_order";
    const isUILanguage = setting.friendlyName === "settings.ui_language";
    const isPhotoGallery = setting.friendlyName === "settings.photo_gallery";
    const isBandwidthProfile = setting.friendlyName === "settings.bandwidth_profile";

    const languageOptions = [
        { value: "en", label: "English - English", image: "united_kingdom.png" },
        { value: "es", label: "Spanish - Español", image: "spain.png" },
        { value: "zh", label: "Chinese - 中文", image: "china.png" },
        { value: "hi", label: "Hindi - हिन्दी", image: "hindi.png" },
        { value: "it", label: "Italian - Italiano", image: "italy.png" },
        { value: "vi", label: "Vietnamese - Tiếng Việt", image: "vietnamese.png" },
        { value: "pl", label: "Polish - Polski", image: "poland.png" },
        { value: "fr", label: "French - Français", image: "france.png" },
        { value: "el", label: "Greek - Ελληνικά", image: "greece.png" },
        { value: "ko", label: "Korean - 한국어", image: "south_korea.png" },
        { value: "ja", label: "Japanese - 日本語", image: "japan.png" },
        { value: "fa", label: "Persian - فارسی", image: "iran.png" },
        { value: "ro", label: "Romanian - Română", image: "romania.png" },
        { value: "pt", label: "Portuguese - Português", image: "portugal.png" },
        { value: "de", label: "German - Deutsch", image: "germany.png" },
        { value: "sw", label: "Swahili - Kiswahili", image: "tanzania.png" },
        { value: "ar", label: "Arabic - العربية", image: "saudi_arabia.png" },
    ];

    const dndDaysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    if (isDndSetting) {
        let summary = "Not configured";

        if (setting.value) {
            try {
                const parsed = JSON.parse(setting.value);
                const callLabel =
                    parsed.CallHandling === "AllowCalls" ? "Allow calls" : "Block calls";

                if (!parsed.isDndEnabled) {
                    summary = "Disabled";
                } else {
                    const startLabel = parsed.start
                        ? dayjs(parsed.start).format("hh:mm A")
                        : "";
                    const endLabel = parsed.end ? dayjs(parsed.end).format("hh:mm A") : "";
                    const timeLabel =
                        startLabel && endLabel ? `${startLabel} - ${endLabel}` : "";

                    const daysLabel = (parsed.selectedDay || [])
                        .map((n) => dndDaysOfWeek[n - 1])
                        .filter(Boolean)
                        .join(", ");

                    summary = callLabel;
                    if (timeLabel) {
                        summary += ` · ${timeLabel}`;
                    }
                    if (daysLabel) {
                        summary += ` · ${daysLabel}`;
                    }
                }
            } catch (e) {
                summary = "Not configured";
            }
        }

        return (
            <div
                style={{
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <div>
                    <strong style={{ fontSize: "14px" }}>Do Not Disturb</strong>
                    <div
                        style={{
                            fontSize: "12px",
                            color: "#666",
                            marginTop: 4,
                        }}
                    >
                        {summary}
                    </div>
                </div>
                <button
                    type="button"
                    className="btn cur-p btn-primary"
                    style={{ fontSize: "12px", padding: "4px 10px" }}
                    onClick={() => {
                        setDndSetting(setting);
                        initializeDndFromSetting(setting);
                        setShowDndModal(true);
                    }}
                >
                    Edit
                </button>
            </div>
        );
    }

    if (isOrderSetting) {
        let ids = [];
        if (setting.value) {
            try {
                const parsed = JSON.parse(setting.value);
                if (Array.isArray(parsed)) {
                    ids = parsed.map((x) => String(x));
                }
            } catch (e) {
                console.error("Failed to parse Order Contact list in row:", e);
            }
        }

        const labels = ids.map((id) => {
            const dev = linkedDevicesMap[id];
            return dev?.shortName || dev?.name || id;
        });

        let summary = "Not configured";
        if (labels.length > 0) {
            const display = labels.slice(0, 4).join(", ");
            summary =
                labels.length > 4
                    ? `${display} … (${labels.length} contacts)`
                    : display;
        }

        return (
            <div
                style={{
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <div>
                    <strong style={{ fontSize: "14px" }}>Order Contact list</strong>
                    <div
                        style={{
                            fontSize: "12px",
                            color: "#666",
                            marginTop: 4,
                        }}
                    >
                        {summary}
                    </div>
                </div>
                <button
                    type="button"
                    className="btn cur-p btn-primary"
                    style={{ fontSize: "12px", padding: "4px 10px" }}
                    onClick={() => {
                        setOrderSetting(setting);
                        setOrderKind?.("legacy");
                        initializeOrderFromSetting(setting);
                        setShowOrderModal(true);
                    }}
                >
                    Edit
                </button>
            </div>
        );
    }

    if (isAddressBookOrderSetting) {
        let ids = [];
        if (setting.value) {
            try {
                const parsed = JSON.parse(setting.value);
                if (Array.isArray(parsed)) {
                    ids = parsed.map((x) => String(x));
                }
            } catch (e) {
                console.error("Failed to parse Address Book Order in row:", e);
            }
        }

        let summary = "Not configured";
        if (ids.length > 0) {
            summary = `${ids.length} contact${ids.length === 1 ? "" : "s"} ordered`;
        }

        return (
            <div
                style={{
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <div>
                    <strong style={{ fontSize: "14px" }}>Address Book Order</strong>
                    <div
                        style={{
                            fontSize: "12px",
                            color: "#666",
                            marginTop: 4,
                        }}
                    >
                        {summary}
                    </div>
                </div>
                <button
                    type="button"
                    className="btn cur-p btn-primary"
                    style={{ fontSize: "12px", padding: "4px 10px" }}
                    disabled={loadingReorder}
                    onClick={() => openAddressBookReorder?.(setting)}
                >
                    {loadingReorder ? "Loading..." : "Edit"}
                </button>
            </div>
        );
    }

    if (isUILanguage) {
        return (
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                    }}
                >
                    <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <select
                        value={setting.value || "en"}
                        onChange={(e) => handleSelectChange(setting, e.target.value)}
                        disabled={isUpdating}
                        style={{
                            flex: 1,
                            padding: "6px 10px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px",
                            outline: "none",
                            backgroundColor: "#fff",
                            cursor: isUpdating ? "not-allowed" : "pointer",
                        }}
                    >
                        {languageOptions.map((lang) => (
                            <option key={lang.value} value={lang.value}>
                                {lang.label}
                            </option>
                        ))}
                    </select>
                    {hasValue && (
                        <ContentCopy
                            onClick={() => copyToClipboard(setting.value)}
                            sx={{
                                cursor: "pointer",
                                fontSize: "1rem",
                                color: "#666",
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    if (isPhotoGallery) {
        let photoSettings = {
            enable_photo_gallery: "false",
            show_clock: "false",
            alternate_clock_photos: "false",
        };

        try {
            if (setting.value) {
                photoSettings = JSON.parse(setting.value);
            }
        } catch (e) {
            console.error("Failed to parse Photo Gallery Settings:", e);
        }

        return (
            <div style={{ marginBottom: 12 }}>
                <strong
                    style={{ fontSize: "14px", display: "block", marginBottom: 8 }}
                >
                    {setting.name}
                </strong>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            paddingLeft: 8,
                        }}
                    >
                        <span style={{ fontSize: "13px" }}>Enable Photo Gallery</span>
                        <Switch
                            checked={photoSettings.enable_photo_gallery === "true"}
                            onChange={(e) =>
                                handlePhotoGalleryChange(
                                    setting,
                                    "enable_photo_gallery",
                                    e.target.checked
                                )
                            }
                            disabled={isUpdating}
                            color="primary"
                            size="small"
                        />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            paddingLeft: 8,
                        }}
                    >
                        <span style={{ fontSize: "13px" }}>Show Clock</span>
                        <Switch
                            checked={photoSettings.show_clock === "true"}
                            onChange={(e) =>
                                handlePhotoGalleryChange(setting, "show_clock", e.target.checked)
                            }
                            disabled={isUpdating}
                            color="primary"
                            size="small"
                        />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            paddingLeft: 8,
                        }}
                    >
                        <span style={{ fontSize: "13px" }}>Alternate Clock & Photos</span>
                        <Switch
                            checked={photoSettings.alternate_clock_photos === "true"}
                            onChange={(e) =>
                                handlePhotoGalleryChange(
                                    setting,
                                    "alternate_clock_photos",
                                    e.target.checked
                                )
                            }
                            disabled={isUpdating}
                            color="primary"
                            size="small"
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (isBoolean) {
        return (
            <div
                style={{
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                    <span style={{ fontSize: "13px", color: "#666" }}>
                        {parseBooleanValue(setting.value) ? "Enabled" : "Disabled"}
                    </span>
                </div>
                <Switch
                    checked={parseBooleanValue(setting.value)}
                    onChange={() => handleToggleSetting(setting)}
                    disabled={isUpdating}
                    color="primary"
                    size="small"
                />
            </div>
        );
    }

    if (isCarerMode) {
        return (
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                    }}
                >
                    <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <select
                        value={setting.value || "off"}
                        onChange={(e) => handleSelectChange(setting, e.target.value)}
                        disabled={isUpdating}
                        style={{
                            flex: 1,
                            padding: "6px 10px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px",
                            outline: "none",
                            backgroundColor: "#fff",
                            cursor: isUpdating ? "not-allowed" : "pointer",
                        }}
                    >
                        <option value="off">Off</option>
                        <option value="on">On</option>
                        <option value="onhidesetting">On (Hide Setting)</option>
                    </select>
                    {hasValue && (
                        <ContentCopy
                            onClick={() => copyToClipboard(setting.value)}
                            sx={{
                                cursor: "pointer",
                                fontSize: "1rem",
                                color: "#666",
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    if (isMaxContacts) {
        return (
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                    }}
                >
                    <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <select
                        value={setting.value || "8"}
                        onChange={(e) => handleSelectChange(setting, e.target.value)}
                        disabled={isUpdating}
                        style={{
                            flex: 1,
                            padding: "6px 10px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px",
                            outline: "none",
                            backgroundColor: "#fff",
                            cursor: isUpdating ? "not-allowed" : "pointer",
                        }}
                    >
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="8">8</option>
                    </select>
                    {hasValue && (
                        <ContentCopy
                            onClick={() => copyToClipboard(setting.value)}
                            sx={{
                                cursor: "pointer",
                                fontSize: "1rem",
                                color: "#666",
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    if (isBandwidthProfile) {
        return (
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                    }}
                >
                    <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <select
                        value={setting.value || "auto"}
                        onChange={(e) => handleSelectChange(setting, e.target.value)}
                        disabled={isUpdating}
                        style={{
                            flex: 1,
                            padding: "6px 10px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px",
                            outline: "none",
                            backgroundColor: "#fff",
                            cursor: isUpdating ? "not-allowed" : "pointer",
                        }}
                    >
                        <option value="auto">High: 640 x 360</option>
                        <option value="medium">Medium: 320 x 180</option>
                        <option value="low">Low: 160 x 120</option>
                    </select>
                    {hasValue && (
                        <ContentCopy
                            onClick={() => copyToClipboard(setting.value)}
                            sx={{
                                cursor: "pointer",
                                fontSize: "1rem",
                                color: "#666",
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: 12 }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 4,
                }}
            >
                <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
            </div>
            {isEditing ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        disabled={isUpdating}
                        placeholder="Enter value"
                        style={{
                            flex: 1,
                            padding: "6px 10px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px",
                            outline: "none",
                        }}
                    />
                    <div style={{ display: "flex", gap: 4 }}>
                        <CheckIcon
                            onClick={() => handleSaveTextSetting(setting)}
                            sx={{
                                cursor: isUpdating ? "not-allowed" : "pointer",
                                color: isUpdating ? "#ccc" : "#4CAF50",
                                fontSize: "1.2rem",
                            }}
                        />
                        <XIcon
                            onClick={handleCancelEdit}
                            sx={{
                                cursor: isUpdating ? "not-allowed" : "pointer",
                                color: isUpdating ? "#ccc" : "#FF5252",
                                fontSize: "1.2rem",
                            }}
                        />
                    </div>
                </div>
            ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                        style={{
                            fontSize: "14px",
                            color: hasValue ? "#333" : "#999",
                            fontStyle: hasValue ? "normal" : "italic",
                        }}
                    >
                        {hasValue ? String(setting.value) : "Not set"}
                    </span>
                    <EditIcon
                        onClick={() => handleEditSetting(setting)}
                        sx={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            color: "#666",
                        }}
                    />
                    {hasValue && (
                        <ContentCopy
                            onClick={() => copyToClipboard(setting.value)}
                            sx={{
                                cursor: "pointer",
                                fontSize: "1rem",
                                color: "#666",
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

function DeviceSettingsTab({
    device,
    onDeviceUpdated,
    selectedOrg,
    linkedDevices = [],
}) {
    const [isEditingKonnektId, setIsEditingKonnektId] = useState(false);
    const [newKonnektId, setNewKonnektId] = useState(
        device.konnektDeviceId || ""
    );
    const [showRenameKonnectIdModal, setShowRenameKonnectIdModal] =
        useState(false);

    const [settings, setSettings] = useState([]);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [editingSettingId, setEditingSettingId] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const [updatingSettings, setUpdatingSettings] = useState({});

    const hiddenSettings = [
        "Subtitle Display",
        "HD Video Call",
        "Welness Score",
        "Maximum wallpaper for a Device",
    ];

    const deviceTypeRaw =
        device?.deviceType || device?.type || device?.device_type || "";
    const deviceType = String(deviceTypeRaw).toLowerCase();
    const isSmartphone =
        deviceType === "android-smartphone" || deviceType === "ios";
    const isKonnektDeviceType = deviceType === "konnekt";

    const rawOrgCodeValue = device?.orgCode || selectedOrg?.code || "";
    const orgCode = rawOrgCodeValue ? String(rawOrgCodeValue) : "";
    const isKonnektOrg = orgCode.toLowerCase() === "konnekt";
    const showOrgWarning = !!orgCode && !isKonnektOrg;

    const isKonnektManaged = isKonnektDeviceType || isKonnektOrg;

    const dndDaysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const dndHours = Array.from({ length: 13 }, (_, i) =>
        i.toString().padStart(2, "0")
    );
    const dndMinutes = Array.from({ length: 60 }, (_, i) =>
        i.toString().padStart(2, "0")
    );
    const dndPeriods = ["AM", "PM"];

    const [showDndModal, setShowDndModal] = useState(false);
    const [dndSetting, setDndSetting] = useState(null);
    const [dndIsEnabled, setDndIsEnabled] = useState(false);
    const [dndStartHour, setDndStartHour] = useState("12");
    const [dndStartMinute, setDndStartMinute] = useState("00");
    const [dndStartPeriod, setDndStartPeriod] = useState("AM");
    const [dndEndHour, setDndEndHour] = useState("12");
    const [dndEndMinute, setDndEndMinute] = useState("00");
    const [dndEndPeriod, setDndEndPeriod] = useState("PM");
    const [dndCheckedDays, setDndCheckedDays] = useState({
        Mon: false,
        Tue: false,
        Wed: false,
        Thu: false,
        Fri: false,
        Sat: false,
        Sun: false,
    });
    const [dndCallHandling, setDndCallHandling] = useState("BlockCalls");

    const [showOrderModal, setShowOrderModal] = useState(false);
    const [orderSetting, setOrderSetting] = useState(null);
    const [orderList, setOrderList] = useState([]);

    // For the shared reorder modal: which kind of order is being edited.
    const [orderKind, setOrderKind] = useState("legacy"); // "legacy" | "addressBook"
    const [addressBookEntries, setAddressBookEntries] = useState([]);
    const [addressBookDeviceImages, setAddressBookDeviceImages] = useState({});
    const [savingOrder, setSavingOrder] = useState(false);
    const [loadingReorder, setLoadingReorder] = useState(false);

    const linkedDevicesMap = React.useMemo(() => {
        const map = {};
        (linkedDevices || []).forEach((d) => {
            if (d && d.id != null) {
                map[String(d.id)] = d;
            }
        });
        return map;
    }, [linkedDevices]);

    const copyToClipboard = (value) => {
        if (value === null || value === undefined || value === "") return;
        navigator.clipboard.writeText(String(value));
        toast.success("Copied");
    };

    const handleEditKonnectId = () => {
        setIsEditingKonnektId(true);
    };

    const handleCancelEditKonnectId = () => {
        setNewKonnektId(device.konnektDeviceId || "");
        setIsEditingKonnektId(false);
    };

    const handleKonnectIdSubmitClick = () => {
        setShowRenameKonnectIdModal(true);
    };

    const handleRenameKonnktIdConfirm = async () => {
        const result = await updateKonnectID(device.id, newKonnektId);
        if (result.success) {
            toast.success("Konnekt Id updated.");
            setIsEditingKonnektId(false);
            setShowRenameKonnectIdModal(false);
            await onDeviceUpdated?.();
        } else {
            toast.error(result.error || "Failed to update Konnekt Id");
        }
    };

    const isBooleanSetting = (value) => {
        if (typeof value === "boolean") return true;
        if (typeof value === "string") {
            const lowerValue = value.toLowerCase();
            return lowerValue === "true" || lowerValue === "false";
        }
        return false;
    };

    const parseBooleanValue = (value) => {
        if (typeof value === "boolean") return value;
        if (typeof value === "string") {
            return value.toLowerCase() === "true";
        }
        return false;
    };

    const handleToggleSetting = async (setting) => {
        const currentValue = parseBooleanValue(setting.value);
        const newValue = !currentValue;

        setUpdatingSettings((prev) => ({ ...prev, [setting.id]: true }));

        try {
            const result = await updateDeviceSetting(
                setting,
                device.id,
                String(newValue)
            );

            if (result.success) {
                toast.success(`${setting.name} updated`);
                setSettings((prev) =>
                    prev.map((s) =>
                        s.id === setting.id ? { ...s, value: String(newValue) } : s
                    )
                );
                await onDeviceUpdated?.();
            } else {
                toast.error(result.error || "Failed to update setting");
            }
        } catch (error) {
            console.error("Error updating setting:", error);
            toast.error("Failed to update setting");
        } finally {
            setUpdatingSettings((prev) => ({ ...prev, [setting.id]: false }));
        }
    };

    const handleEditSetting = (setting) => {
        setEditingSettingId(setting.id);
        setEditingValue(setting.value || "");
    };

    const handleCancelEdit = () => {
        setEditingSettingId(null);
        setEditingValue("");
    };

    const handleSaveTextSetting = async (setting) => {
        if (editingValue === setting.value) {
            handleCancelEdit();
            return;
        }

        setUpdatingSettings((prev) => ({ ...prev, [setting.id]: true }));

        try {
            const result = await updateDeviceSetting(
                setting,
                device.id,
                editingValue
            );

            if (result.success) {
                toast.success(`${setting.name} updated`);
                setSettings((prev) =>
                    prev.map((s) =>
                        s.id === setting.id ? { ...s, value: editingValue } : s
                    )
                );
                setEditingSettingId(null);
                setEditingValue("");
                await onDeviceUpdated?.();
            } else {
                toast.error(result.error || "Failed to update setting");
            }
        } catch (error) {
            console.error("Error updating setting:", error);
            toast.error("Failed to update setting");
        } finally {
            setUpdatingSettings((prev) => ({ ...prev, [setting.id]: false }));
        }
    };

    const handleSelectChange = async (setting, newValue) => {
        setUpdatingSettings((prev) => ({ ...prev, [setting.id]: true }));

        try {
            const result = await updateDeviceSetting(setting, device.id, newValue);

            if (result.success) {
                toast.success(`${setting.name} updated`);
                setSettings((prev) =>
                    prev.map((s) =>
                        s.id === setting.id ? { ...s, value: newValue } : s
                    )
                );
                await onDeviceUpdated?.();
            } else {
                toast.error(result.error || "Failed to update setting");
            }
        } catch (error) {
            console.error("Error updating setting:", error);
            toast.error("Failed to update setting");
        } finally {
            setUpdatingSettings((prev) => ({ ...prev, [setting.id]: false }));
        }
    };

    const handlePhotoGalleryChange = async (setting, field, newValue) => {
        let currentObj = {
            enable_photo_gallery: "false",
            show_clock: "false",
            alternate_clock_photos: "false",
        };

        try {
            if (setting.value) {
                currentObj = JSON.parse(setting.value);
            }
        } catch (e) {
            console.error("Failed to parse Photo Gallery Settings:", e);
        }

        currentObj[field] = String(newValue);
        const newValueStr = JSON.stringify(currentObj);

        setUpdatingSettings((prev) => ({ ...prev, [setting.id]: true }));

        try {
            const result = await updateDeviceSetting(
                setting,
                device.id,
                newValueStr
            );

            if (result.success) {
                toast.success(`${setting.name} updated`);
                setSettings((prev) =>
                    prev.map((s) =>
                        s.id === setting.id ? { ...s, value: newValueStr } : s
                    )
                );
                await onDeviceUpdated?.();
            } else {
                toast.error(result.error || "Failed to update setting");
            }
        } catch (error) {
            console.error("Error updating setting:", error);
            toast.error("Failed to update setting");
        } finally {
            setUpdatingSettings((prev) => ({ ...prev, [setting.id]: false }));
        }
    };

    const initializeDndFromSetting = (setting) => {
        let parsed = null;

        try {
            parsed = setting.value ? JSON.parse(setting.value) : null;
        } catch (e) {
            console.error("Failed to parse DND setting value:", e);
        }

        if (!parsed) {
            const nowStart = new Date();
            nowStart.setHours(0, 0, 0, 0);
            const nowEnd = new Date();
            nowEnd.setHours(12, 0, 0, 0);
            parsed = {
                start: nowStart.toISOString(),
                end: nowEnd.toISOString(),
                selectedDay: [],
                isDndEnabled: false,
                CallHandling: "BlockCalls",
            };
        }

        const { start, end, selectedDay, isDndEnabled, CallHandling } = parsed;

        const startDt = dayjs(start);
        const endDt = dayjs(end);

        setDndIsEnabled(!!isDndEnabled);
        setDndStartHour(startDt.format("hh"));
        setDndStartMinute(startDt.format("mm"));
        setDndStartPeriod(startDt.format("A"));
        setDndEndHour(endDt.format("hh"));
        setDndEndMinute(endDt.format("mm"));
        setDndEndPeriod(endDt.format("A"));

        const selected = {};
        dndDaysOfWeek.forEach((d) => {
            selected[d] = false;
        });

        (selectedDay || []).forEach((num) => {
            const idx = num - 1;
            if (idx >= 0 && idx < dndDaysOfWeek.length) {
                const key = dndDaysOfWeek[idx];
                selected[key] = true;
            }
        });
        setDndCheckedDays(selected);

        setDndCallHandling(
            CallHandling === "AllowCalls" ? "AllowCalls" : "BlockCalls"
        );
    };

    const handleSaveDnd = async () => {
        if (!dndSetting || !device?.id) {
            setShowDndModal(false);
            return;
        }

        try {
            const startDate = new Date();
            let sh = parseInt(dndStartHour || "0", 10);
            if (dndStartPeriod === "PM" && sh !== 12) {
                sh = sh + 12;
            }
            if (dndStartPeriod === "AM" && sh === 12) {
                sh = 0;
            }
            const sm = parseInt(dndStartMinute || "0", 10);
            startDate.setHours(sh);
            startDate.setMinutes(sm);
            startDate.setSeconds(0, 0);

            const endDate = new Date();
            let eh = parseInt(dndEndHour || "0", 10);
            if (dndEndPeriod === "PM" && eh !== 12) {
                eh = eh + 12;
            }
            if (dndEndPeriod === "AM" && eh === 12) {
                eh = 0;
            }
            const em = parseInt(dndEndMinute || "0", 10);
            endDate.setHours(eh);
            endDate.setMinutes(em);
            endDate.setSeconds(0, 0);

            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                toast.error("Invalid start or end time");
                return;
            }

            const selectedDayNumbers = dndDaysOfWeek
                .filter((dayKey) => dndCheckedDays[dayKey])
                .map((dayKey) => dndDaysOfWeek.indexOf(dayKey) + 1);

            const valueObj = {
                start: startDate.toISOString(),
                end: endDate.toISOString(),
                selectedDay: selectedDayNumbers,
                isDndEnabled: dndIsEnabled,
                CallHandling: dndCallHandling,
            };

            const valueStr = JSON.stringify(valueObj);

            const result = await updateDeviceSetting(dndSetting, device.id, valueStr);
            if (result.success) {
                toast.success("Do Not Disturb updated");
                setSettings((prev) =>
                    prev.map((s) =>
                        s.id === dndSetting.id ? { ...s, value: valueStr } : s
                    )
                );
                await onDeviceUpdated?.();
                setShowDndModal(false);
            } else {
                toast.error(result.error || "Failed to update Do Not Disturb");
            }
        } catch (error) {
            console.error("Error updating Do Not Disturb:", error);
            toast.error("Failed to update Do Not Disturb");
        }
    };

    const initializeOrderFromSetting = (setting) => {
        if (!setting) {
            setOrderList([]);
            return;
        }

        let parsedIds = [];
        try {
            if (setting.value) {
                const v = JSON.parse(setting.value);
                if (Array.isArray(v)) {
                    parsedIds = v.map((x) => String(x));
                }
            }
        } catch (e) {
            console.error("Failed to parse Order Contact list value:", e);
        }

        if (
            (!parsedIds || parsedIds.length === 0) &&
            linkedDevices &&
            linkedDevices.length > 0
        ) {
            parsedIds = linkedDevices.map((d) => String(d.id));
        }

        setOrderList(parsedIds);
    };

    const handleSaveOrder = async (orderedIdsArg) => {
        if (!orderSetting || !device?.id) {
            setShowOrderModal(false);
            return;
        }

        const orderedIds = Array.isArray(orderedIdsArg) ? orderedIdsArg : orderList;

        try {
            setSavingOrder(true);
            const valueStr = JSON.stringify(orderedIds || []);
            const result = await updateDeviceSetting(
                orderSetting,
                device.id,
                valueStr
            );
            if (result.success) {
                setOrderList(orderedIds);
                toast.success("Contact order updated");
                setSettings((prev) =>
                    prev.map((s) =>
                        s.id === orderSetting.id ? { ...s, value: valueStr } : s
                    )
                );
                await onDeviceUpdated?.();
                setShowOrderModal(false);
            } else {
                toast.error(result.error || "Failed to update contact order");
            }
        } catch (error) {
            console.error("Error updating contact order:", error);
            toast.error("Failed to update contact order");
        } finally {
            setSavingOrder(false);
        }
    };

    const openAddressBookReorder = async (setting) => {
        if (!device?.id) return;
        setLoadingReorder(true);
        try {
            const booksRes = await fetchAddressBooksForDevice(device.id);
            if (!booksRes.success) {
                toast.error(booksRes.error || "Failed to load address books");
                return;
            }
            const books = Array.isArray(booksRes.data) ? booksRes.data : [];
            const defaultBook =
                books.find((b) => b.systemKey === "default") || books[0] || null;
            if (!defaultBook) {
                toast.error("No address book found for this device");
                return;
            }

            const entriesRes = await fetchAddressBookEntries(defaultBook.id);
            if (!entriesRes.success) {
                toast.error(entriesRes.error || "Failed to load address book entries");
                return;
            }
            const entries = (Array.isArray(entriesRes.data) ? entriesRes.data : [])
                .filter((e) => e.isActive !== false);
            setAddressBookEntries(entries);

            // Build initial order: saved order first (filtered to live entries),
            // then any entries not yet in the saved order appended at the end.
            let savedIds = [];
            try {
                const parsed = JSON.parse(setting.value || "[]");
                if (Array.isArray(parsed)) savedIds = parsed.map(String);
            } catch (e) {
                console.warn("Failed to parse address book order:", e);
            }
            const liveIds = new Set(entries.map((e) => String(e.id)));
            const filtered = savedIds.filter((id) => liveIds.has(id));
            entries.forEach((e) => {
                if (!filtered.includes(String(e.id))) filtered.push(String(e.id));
            });

            // Fetch device images for video contacts (so the modal can show avatars).
            const videoTargetIds = [
                ...new Set(
                    entries
                        .filter((e) => e.entryType === "video_contact" && e.targetDeviceId)
                        .map((e) => e.targetDeviceId)
                ),
            ];
            const imageUpdates = {};
            await Promise.all(
                videoTargetIds.map(async (id) => {
                    const res = await fetchDeviceImage(id);
                    imageUpdates[id] = res.success
                        ? res.data?.profilePicture || null
                        : null;
                })
            );
            if (Object.keys(imageUpdates).length > 0) {
                setAddressBookDeviceImages((prev) => ({ ...prev, ...imageUpdates }));
            }

            setOrderSetting(setting);
            setOrderKind("addressBook");
            setOrderList(filtered);
            setShowOrderModal(true);
        } finally {
            setLoadingReorder(false);
        }
    };

    const reorderItems = useMemo(() => {
        if (orderKind === "legacy") {
            return orderList.map((id) => {
                const dev = linkedDevicesMap[id];
                return {
                    id,
                    label: dev?.shortName || dev?.name || String(id),
                    avatarSrc: dev?.profilePicture
                        ? `data:image/png;base64,${dev.profilePicture}`
                        : null,
                };
            });
        }
        // address book
        const entryById = {};
        addressBookEntries.forEach((e) => {
            entryById[String(e.id)] = e;
        });
        return orderList.map((id) => {
            const e = entryById[id];
            if (!e) return { id, label: id, avatarSrc: null };
            const isVideo = e.entryType === "video_contact";
            let avatarSrc = null;
            if (isVideo) {
                const base64 = addressBookDeviceImages[e.targetDeviceId];
                if (base64) avatarSrc = `data:image/png;base64,${base64}`;
            } else if (e.avatarUrl) {
                avatarSrc = `${e.avatarUrl}${e.avatarUpdatedAt
                    ? `?v=${encodeURIComponent(e.avatarUpdatedAt)}`
                    : ""}`;
            }
            return {
                id,
                label: e.displayName || String(id),
                avatarSrc,
            };
        });
    }, [
        orderKind,
        orderList,
        linkedDevicesMap,
        addressBookEntries,
        addressBookDeviceImages,
    ]);



    const SettingRow = ({ setting }) => {
        const isBoolean = isBooleanSetting(setting.value);
        const isEditing = editingSettingId === setting.id;
        const isUpdating = updatingSettings[setting.id];
        const hasValue =
            setting.value === 0 ||
            setting.value === false ||
            (setting.value !== null &&
                setting.value !== undefined &&
                String(setting.value).trim() !== "");

        const isCarerMode = setting.friendlyName === "settings.group_leader.carer.mode";
        const isMaxContacts = setting.friendlyName === "settings.max.contacts_per_page";
        const isDndSetting = setting.friendlyName === "settings.dnd";
        const isOrderSetting = setting.friendlyName === "settings.device_contact_order";
        const isBandwidthProfile = setting.friendlyName === "settings.bandwidth_profile";

        if (isDndSetting) {
            let summary = "Not configured";

            if (setting.value) {
                try {
                    const parsed = JSON.parse(setting.value);
                    const callLabel =
                        parsed.CallHandling === "AllowCalls"
                            ? "Allow calls"
                            : "Block calls";

                    if (!parsed.isDndEnabled) {
                        summary = "Disabled";
                    } else {
                        const startLabel = parsed.start
                            ? dayjs(parsed.start).format("hh:mm A")
                            : "";
                        const endLabel = parsed.end
                            ? dayjs(parsed.end).format("hh:mm A")
                            : "";
                        const timeLabel =
                            startLabel && endLabel ? `${startLabel} - ${endLabel}` : "";

                        const daysLabel = (parsed.selectedDay || [])
                            .map((n) => dndDaysOfWeek[n - 1])
                            .filter(Boolean)
                            .join(", ");

                        summary = callLabel;
                        if (timeLabel) {
                            summary += ` · ${timeLabel}`;
                        }
                        if (daysLabel) {
                            summary += ` · ${daysLabel}`;
                        }
                    }
                } catch (e) {
                    summary = "Not configured";
                }
            }

            return (
                <div
                    style={{
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <div>
                        <strong style={{ fontSize: "14px" }}>{setting.name}</strong>
                        <div
                            style={{
                                fontSize: "12px",
                                color: "#666",
                                marginTop: 4,
                            }}
                        >
                            {summary}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn cur-p btn-primary"
                        style={{ fontSize: "12px", padding: "4px 10px" }}
                        onClick={() => {
                            setDndSetting(setting);
                            initializeDndFromSetting(setting);
                            setShowDndModal(true);
                        }}
                    >
                        Edit
                    </button>
                </div>
            );
        }

        if (isOrderSetting) {
            let ids = [];
            if (setting.value) {
                try {
                    const parsed = JSON.parse(setting.value);
                    if (Array.isArray(parsed)) {
                        ids = parsed.map((x) => String(x));
                    }
                } catch (e) {
                    console.error("Failed to parse Order Contact list in row:", e);
                }
            }

            const labels = ids.map((id) => {
                const dev = linkedDevicesMap[id];
                return dev?.shortName || dev?.name || id;
            });

            let summary = "Not configured";
            if (labels.length > 0) {
                const display = labels.slice(0, 4).join(", ");
                summary =
                    labels.length > 4
                        ? `${display} … (${labels.length} contacts)`
                        : display;
            }

            return (
                <div
                    style={{
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <div>
                        <strong style={{ fontSize: "14px" }}>{setting.name}</strong>
                        <div
                            style={{
                                fontSize: "12px",
                                color: "#666",
                                marginTop: 4,
                            }}
                        >
                            {summary}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn cur-p btn-primary"
                        style={{ fontSize: "12px", padding: "4px 10px" }}
                        onClick={() => {
                            setOrderSetting(setting);
                            initializeOrderFromSetting(setting);
                            setShowOrderModal(true);
                        }}
                    >
                        Edit
                    </button>
                </div>
            );
        }

        if (isBoolean) {
            return (
                <div
                    style={{
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                        <span style={{ fontSize: "13px", color: "#666" }}>
                            {parseBooleanValue(setting.value) ? "Enabled" : "Disabled"}
                        </span>
                    </div>
                    <Switch
                        checked={parseBooleanValue(setting.value)}
                        onChange={() => handleToggleSetting(setting)}
                        disabled={isUpdating}
                        color="primary"
                        size="small"
                    />
                </div>
            );
        }

        if (isCarerMode) {
            return (
                <div style={{ marginBottom: 12 }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 4,
                        }}
                    >
                        <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <select
                            value={setting.value || "off"}
                            onChange={(e) => handleSelectChange(setting, e.target.value)}
                            disabled={isUpdating}
                            style={{
                                flex: 1,
                                padding: "6px 10px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "14px",
                                outline: "none",
                                backgroundColor: "#fff",
                                cursor: isUpdating ? "not-allowed" : "pointer",
                            }}
                        >
                            <option value="off">Off</option>
                            <option value="on">On</option>
                            <option value="onhidesetting">On (Hide Setting)</option>
                        </select>
                        {hasValue && (
                            <ContentCopy
                                onClick={() => copyToClipboard(setting.value)}
                                sx={{
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                    color: "#666",
                                }}
                            />
                        )}
                    </div>
                </div>
            );
        }

        if (isMaxContacts) {
            return (
                <div style={{ marginBottom: 12 }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 4,
                        }}
                    >
                        <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <select
                            value={setting.value || "8"}
                            onChange={(e) => handleSelectChange(setting, e.target.value)}
                            disabled={isUpdating}
                            style={{
                                flex: 1,
                                padding: "6px 10px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "14px",
                                outline: "none",
                                backgroundColor: "#fff",
                                cursor: isUpdating ? "not-allowed" : "pointer",
                            }}
                        >
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="8">8</option>
                        </select>
                        {hasValue && (
                            <ContentCopy
                                onClick={() => copyToClipboard(setting.value)}
                                sx={{
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                    color: "#666",
                                }}
                            />
                        )}
                    </div>
                </div>
            );
        }

        if (isBandwidthProfile) {
            return (
                <div style={{ marginBottom: 12 }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 4,
                        }}
                    >
                        <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <select
                            value={setting.value || "auto"}
                            onChange={(e) => handleSelectChange(setting, e.target.value)}
                            disabled={isUpdating}
                            style={{
                                flex: 1,
                                padding: "6px 10px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "14px",
                                outline: "none",
                                backgroundColor: "#fff",
                                cursor: isUpdating ? "not-allowed" : "pointer",
                            }}
                        >
                            <option value="auto">High: 640 x 360</option>
                            <option value="medium">Medium: 320 x 180</option>
                            <option value="low">Low: 160 x 120</option>
                        </select>
                        {hasValue && (
                            <ContentCopy
                                onClick={() => copyToClipboard(setting.value)}
                                sx={{
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                    color: "#666",
                                }}
                            />
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                    }}
                >
                    <strong style={{ fontSize: "14px" }}>{setting.name}:</strong>
                </div>
                {isEditing ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            disabled={isUpdating}
                            placeholder="Enter value"
                            style={{
                                flex: 1,
                                padding: "6px 10px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "14px",
                                outline: "none",
                            }}
                        />
                        <div style={{ display: "flex", gap: 4 }}>
                            <CheckIcon
                                onClick={() => handleSaveTextSetting(setting)}
                                sx={{
                                    cursor: isUpdating ? "not-allowed" : "pointer",
                                    color: isUpdating ? "#ccc" : "#4CAF50",
                                    fontSize: "1.2rem",
                                }}
                            />
                            <XIcon
                                onClick={handleCancelEdit}
                                sx={{
                                    cursor: isUpdating ? "not-allowed" : "pointer",
                                    color: isUpdating ? "#ccc" : "#FF5252",
                                    fontSize: "1.2rem",
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span
                            style={{
                                fontSize: "14px",
                                color: hasValue ? "#333" : "#999",
                                fontStyle: hasValue ? "normal" : "italic",
                            }}
                        >
                            {hasValue ? String(setting.value) : "Not set"}
                        </span>
                        <EditIcon
                            onClick={() => handleEditSetting(setting)}
                            sx={{
                                cursor: "pointer",
                                fontSize: "1rem",
                                color: "#666",
                            }}
                        />
                        {hasValue && (
                            <ContentCopy
                                onClick={() => copyToClipboard(setting.value)}
                                sx={{
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                    color: "#666",
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (!device?.id) {
            setSettings([]);
            return;
        }

        const loadSettings = async () => {
            setSettingsLoading(true);
            try {
                const result = await fetchDeviceSettings(device.id);
                if (result.success) {
                    let filteredSettings = (result.data || []).filter(
                        (s) => !hiddenSettings.includes(s.name)
                    );

                    if (isSmartphone) {
                        if(deviceType === "android-smartphone"){
                            filteredSettings = filteredSettings.filter(
                            (s) =>
                                s.friendlyName === "settings.dnd" ||
                                s.friendlyName === "settings.device_contact_order" ||
                                s.friendlyName === "settings.device.logs" ||
                                s.friendlyName === "settings.agora.logs" ||
                                s.friendlyName === "settings.bandwidth_profile"
                        );
                        }else if (deviceType === "ios"){
                            filteredSettings = filteredSettings.filter(
                            (s) =>
                                s.friendlyName === "settings.dnd" ||
                                s.friendlyName === "settings.device_contact_order" ||
                                s.friendlyName === "settings.bandwidth_profile"
                        );
                        }
                        
                    }

                    const sortedSettings = filteredSettings.sort((a, b) =>
                        a.name.localeCompare(b.name)
                    );

                    setSettings(sortedSettings);

                    const dnd = sortedSettings.find((s) => s.friendlyName === "settings.dnd");
                    if (dnd) {
                        setDndSetting(dnd);
                        initializeDndFromSetting(dnd);
                    } else {
                        setDndSetting(null);
                    }

                    const order = sortedSettings.find(
                        (s) => s.friendlyName === "settings.device_contact_order"
                    );
                    if (order) {
                        setOrderSetting(order);
                    } else {
                        setOrderSetting(null);
                        setOrderList([]);
                    }
                } else {
                    toast.error(result.error || "Failed to load device settings");
                }
            } catch (err) {
                console.error("Error fetching device settings:", err);
                toast.error("Failed to load device settings");
            } finally {
                setSettingsLoading(false);
            }
        };

        loadSettings();
    }, [device?.id, isSmartphone]);

    if (isKonnektManaged) {
        return (
            <div className="padding_infor_info" style={{ paddingTop: 20 }}>
                <p style={{ fontWeight: "bold", color: "red" }}>
                    Please contact Konnekt support team members or go to SugarCRM to
                    change any settings for now
                </p>
            </div>
        );
    }

    return (
        <div className="padding_infor_info" style={{ paddingTop: 20 }}>
            {showOrgWarning && (
                <p style={{ marginBottom: 15, fontWeight: "bold", color: "red" }}>
                    The device is enrolled with "{orgCode}", please contact them before
                    you change any settings on this device.
                </p>
            )}

            {isKonnektOrg && (
                <div style={{ marginBottom: 25 }}>
                    <label style={{ fontWeight: 700, display: "block" }}>
                        Videophone Profile Name (Konnekt Id)
                    </label>
                    {isEditingKonnektId ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                                type="text"
                                className="form-control"
                                value={newKonnektId}
                                onChange={(e) => setNewKonnektId(e.target.value)}
                                style={{ maxWidth: 320 }}
                            />
                            <div style={{ display: "flex", gap: 8 }}>
                                <span
                                    onClick={handleKonnectIdSubmitClick}
                                    style={{ cursor: "pointer", color: "#4CAF50" }}
                                >
                                    <CheckIcon sx={{ fontSize: "1.2rem" }} />
                                </span>
                                <span
                                    onClick={handleCancelEditKonnectId}
                                    style={{ cursor: "pointer", color: "#FF5252" }}
                                >
                                    <XIcon sx={{ fontSize: "1.2rem" }} />
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span>{device.konnektDeviceId || "N/A"}</span>
                            <EditIcon
                                onClick={handleEditKonnectId}
                                sx={{ color: "black", cursor: "pointer" }}
                            />
                            {device.konnektDeviceId && (
                                <ContentCopy
                                    onClick={() => copyToClipboard(device.konnektDeviceId)}
                                    sx={{ color: "black", cursor: "pointer" }}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}

            <div
                style={{
                    marginTop: 10,
                    padding: 20,
                    borderRadius: 6,
                    border: "1px solid #eee",
                    backgroundColor: "#fafafa",
                    fontSize: 14,
                }}
            >
                <h5 style={{ marginTop: 0, marginBottom: 15 }}>Per-device Settings</h5>

                {settingsLoading ? (
                    <div style={{ padding: "20px 0", textAlign: "center" }}>
                        <i
                            className="fa fa-spinner fa-spin"
                            style={{ fontSize: "24px", marginRight: 8, color: "#007bff" }}
                        />
                        <p style={{ marginTop: 10, color: "#666" }}>Loading settings...</p>
                    </div>
                ) : settings.length === 0 ? (
                    <p
                        style={{
                            margin: 0,
                            color: "#777",
                            textAlign: "center",
                            padding: "20px 0",
                        }}
                    >
                        No settings found.
                    </p>
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                            gap: "20px",
                        }}
                    >
                        {settings.map((setting) => (
                            <div key={setting.id}>
                                <SettingRowComponent
                                    setting={setting}
                                    editingSettingId={editingSettingId}
                                    editingValue={editingValue}
                                    setEditingValue={setEditingValue}
                                    updatingSettings={updatingSettings}
                                    handleToggleSetting={handleToggleSetting}
                                    handleEditSetting={handleEditSetting}
                                    handleCancelEdit={handleCancelEdit}
                                    handleSaveTextSetting={handleSaveTextSetting}
                                    handleSelectChange={handleSelectChange}
                                    copyToClipboard={copyToClipboard}
                                    parseBooleanValue={parseBooleanValue}
                                    isBooleanSetting={isBooleanSetting}
                                    setDndSetting={setDndSetting}
                                    initializeDndFromSetting={initializeDndFromSetting}
                                    setShowDndModal={setShowDndModal}
                                    setOrderSetting={setOrderSetting}
                                    initializeOrderFromSetting={initializeOrderFromSetting}
                                    setShowOrderModal={setShowOrderModal}
                                    setOrderKind={setOrderKind}
                                    openAddressBookReorder={openAddressBookReorder}
                                    loadingReorder={loadingReorder}
                                    linkedDevicesMap={linkedDevicesMap}
                                    handlePhotoGalleryChange={handlePhotoGalleryChange}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showRenameKonnectIdModal && (
                <div className="modal" role="dialog" style={{ display: "block" }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: 400 }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Rename Konnekt Id</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowRenameKonnectIdModal(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to rename this device konnekt Id from "
                                    {device.konnektDeviceId}" to "{newKonnektId}"?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={() => setShowRenameKonnectIdModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-primary"
                                    onClick={handleRenameKonnktIdConfirm}
                                >
                                    Rename
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDndModal && (
                <div className="modal" role="dialog" style={{ display: "block" }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: 500 }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Do Not Disturb</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowDndModal(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <div
                                    style={{
                                        marginBottom: 16,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <span>Enable Do Not Disturb</span>
                                    <Switch
                                        checked={dndIsEnabled}
                                        onChange={(e) => setDndIsEnabled(e.target.checked)}
                                        color="primary"
                                    />
                                </div>

                                {dndIsEnabled && (
                                    <>
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ marginBottom: 6 }}>Call handling</div>
                                            <div style={{ display: "flex", gap: 16 }}>
                                                <label style={{ fontSize: 14 }}>
                                                    <input
                                                        type="radio"
                                                        name="dnd-call-handling"
                                                        value="BlockCalls"
                                                        checked={dndCallHandling === "BlockCalls"}
                                                        onChange={() => setDndCallHandling("BlockCalls")}
                                                        style={{ marginRight: 6 }}
                                                    />
                                                    Block calls
                                                </label>
                                                <label style={{ fontSize: 14 }}>
                                                    <input
                                                        type="radio"
                                                        name="dnd-call-handling"
                                                        value="AllowCalls"
                                                        checked={dndCallHandling === "AllowCalls"}
                                                        onChange={() => setDndCallHandling("AllowCalls")}
                                                        style={{ marginRight: 6 }}
                                                    />
                                                    Allow calls
                                                </label>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ marginBottom: 6 }}>Start time</div>
                                            <div style={{ display: "flex", gap: 8 }}>
                                                <select
                                                    className="form-control"
                                                    style={{ width: "90px" }}
                                                    value={dndStartHour}
                                                    onChange={(e) => setDndStartHour(e.target.value)}
                                                >
                                                    {dndHours.map((h) => (
                                                        <option key={h} value={h}>
                                                            {h}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span>:</span>
                                                <select
                                                    className="form-control"
                                                    style={{ width: "90px" }}
                                                    value={dndStartMinute}
                                                    onChange={(e) => setDndStartMinute(e.target.value)}
                                                >
                                                    {dndMinutes.map((m) => (
                                                        <option key={m} value={m}>
                                                            {m}
                                                        </option>
                                                    ))}
                                                </select>
                                                <select
                                                    className="form-control"
                                                    style={{ width: "90px" }}
                                                    value={dndStartPeriod}
                                                    onChange={(e) => setDndStartPeriod(e.target.value)}
                                                >
                                                    {dndPeriods.map((p) => (
                                                        <option key={p} value={p}>
                                                            {p}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ marginBottom: 6 }}>End time</div>
                                            <div style={{ display: "flex", gap: 8 }}>
                                                <select
                                                    className="form-control"
                                                    style={{ width: "90px" }}
                                                    value={dndEndHour}
                                                    onChange={(e) => setDndEndHour(e.target.value)}
                                                >
                                                    {dndHours.map((h) => (
                                                        <option key={h} value={h}>
                                                            {h}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span>:</span>
                                                <select
                                                    className="form-control"
                                                    style={{ width: "90px" }}
                                                    value={dndEndMinute}
                                                    onChange={(e) => setDndEndMinute(e.target.value)}
                                                >
                                                    {dndMinutes.map((m) => (
                                                        <option key={m} value={m}>
                                                            {m}
                                                        </option>
                                                    ))}
                                                </select>
                                                <select
                                                    className="form-control"
                                                    style={{ width: "90px" }}
                                                    value={dndEndPeriod}
                                                    onChange={(e) => setDndEndPeriod(e.target.value)}
                                                >
                                                    {dndPeriods.map((p) => (
                                                        <option key={p} value={p}>
                                                            {p}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <div style={{ marginBottom: 6 }}>Active days</div>
                                            <div
                                                style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                                            >
                                                {dndDaysOfWeek.map((day) => (
                                                    <label key={day} style={{ fontSize: 13, minWidth: 60 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={dndCheckedDays[day]}
                                                            onChange={(e) =>
                                                                setDndCheckedDays((prev) => ({
                                                                    ...prev,
                                                                    [day]: e.target.checked,
                                                                }))
                                                            }
                                                            style={{ marginRight: 4 }}
                                                        />
                                                        {day}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={() => setShowDndModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-primary"
                                    onClick={handleSaveDnd}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ContactsReorderModal
                open={showOrderModal}
                items={reorderItems}
                maxPerPage={(() => {
                    const ms = settings.find(
                        (s) => s.friendlyName === "settings.max.contacts_per_page"
                    );
                    return ms && ms.value ? parseInt(ms.value, 10) || 8 : 8;
                })()}
                saving={savingOrder}
                onClose={() => setShowOrderModal(false)}
                onSave={handleSaveOrder}
            />
        </div>
    );
}

export default DeviceSettingsTab;